<template>
  <div
    class="membership-benefits"
    data-ui="membership-benefits"
  >
    <section
      v-if="showHomepageHero"
      class="membership-benefits__hero-wrapper"
    >
      <homepage-hero-component
        data-ui="homepage-hero-wrapper"
        v-bind="homepageHero"
        :class="computedHeroClasses"
      >
        <template v-if="computedHeroSlot?.Component">
          <component
            :is="computedHeroSlot.Component"
            v-bind="computedHeroSlot.model"
          />
        </template>
        <template #inset-box>
          <InsetBoxMember
            v-if="isLoggedInMember"
            v-bind="insetBoxMemberProps"
          />
          <InsetBoxNonMember
            v-else
            v-bind="insetBoxNonMemberProps"
            @error="handleError"
            @success="handleSuccess"
            @warn="handleWarn"
          />
        </template>
      </homepage-hero-component>
    </section>
    <member-promo-group
      v-if="memberPromoGroupModel"
      v-bind="memberPromoGroupModel"
    />
    <coupon-placement
      v-if="!!memberCouponModel"
      v-bind="memberCouponModel"
      class="member-relaunch__container--narrow"
      data-ui="coupon-placement"
    />
    <mabc-promo
      v-if="showMabcPromo"
      v-bind="mabcPromoModel"
      data-ui="mabc-promo"
      :screen-width="screenWidth"
      :membership-in-cart="membershipAddedToCart"
      @error="handleError"
      @success="handleSuccess"
      @warn="handleWarn"
    />
    <membership-pillars
      v-if="memberMomemtEmbargoReleased"
      @modal-open="(model) => handleSharedModalOpen('pillar', model)"
    />
    <membership-coupons v-if="showMembershipCoupons" />
    <member-benefits
      v-if="showMemberBenefits"
      v-bind="memberBenefitsModel"
      id="perks"
      data-ui="member-benefits"
      @modal-open="(model) => handleSharedModalOpen('featured-benefit', model)"
    />
    <filmstrip-carousel
      v-if="showFilmstrip"
      v-bind="filmstripModel"
    />
    <member-story
      v-if="showMemberStory"
      v-bind="memberStory"
      data-ui="member-story"
    />
    <overlapping-callout
      v-if="showResupply"
      v-bind="resupplyModel"
      class="ol-callout--used-gear"
    />
    <credit-card-callout
      v-if="showMemberCreditCardCallout"
      :tracking-id="trackingId"
      v-bind="creditCardCalloutModel"
      data-ui="credit-card-callout-member"
      :is-limited-time-offer-active="showLimitedTimeOffer"
    />
    <overlapping-callout
      v-if="memberMomemtEmbargoReleased"
      v-bind="memberMadeModel"
      class="ol-callout--member-made"
    />
    <member-quote
      v-if="showMemberQuote"
      v-bind="memberQuote"
      :membership-in-cart="membershipAddedToCart"
      data-ui="member-quote"
      class="membership-benefits__member-quote"
      @error="handleError"
      @success="handleSuccess"
      @warn="handleWarn"
    />
    <can-promo
      v-if="showCanPromo"
      data-ui="can-promo"
    />
    <frequently-asked-questions
      v-if="faq"
      id="faq"
      :faq="faq"
      data-analytics-config="exclude-all"
      data-ui="faq"
      @toggle="handleFAQClick"
    />
    <benefits-add-to-cart
      v-if="showAddMembershipToCartModule"
      :membership-in-cart="membershipAddedToCart"
      class="membership-benefits__add-membership"
      data-ui="benefits-add-to-cart"
      @error="handleError"
      @success="handleSuccess"
      @warn="handleWarn"
    />
    <added-to-cart-modal
      v-if="isClient && enableModalBuyNow"
      :is-open-override="atcModalOpenOverride"
      location="member benefits"
      @close="handleClose"
    />
    <RewardTerms
      v-if="showMemberRewardTerms"
      v-bind="memberRewardTerms"
    />
    <CdrModal
      v-if="isClient"
      v-bind="modal?.containerModel"
      :opened="modal.isOpen"
      @closed="handleModalClose"
    >
      <FeaturedBenefitModal
        v-if="modal.type === 'featured-benefit'"
        v-bind="modal.contentModel"
      />
      <PillarModal
        v-if="modal.type === 'pillar'"
        v-bind="modal.contentModel"
      />
    </CdrModal>
  </div>
</template>

<script>
import FrequentlyAskedQuestions from '@rei/frequently-asked-questions';
import { CdrModal } from '@rei/cedar';
import convertToHtml from '@rei/rich-text-renderer';
import { useDynamicContent } from '@rei/cs-preview-fetch';
import AddedToCartModal from '../../common/components/added-to-cart-modal/AddedToCartModal.vue';
import BenefitsAddToCart from './components/BenefitsAddToCart.vue';
import CountdownBanner from '../../common/components/countdown-banner/CountdownBanner.vue';
import CouponPlacement from './components/CouponPlacement.vue';
import CreditCardCallout from '../../common/components/mastercard-callout-component/CreditCardCallout.vue';
import FilmstripCarousel from '../../common/components/filmstrip/Filmstrip.vue';
import GenericHeroLead from '../../common/components/hero-component/GenericHeroLead.vue';
import HomepageHeroComponent from '../../common/components/hero-component/HomepageHeroComponent.vue';
import InsetBoxMember from '../../common/components/hero-component/InsetBoxMember.vue';
import InsetBoxNonMember from '../../common/components/hero-component/InsetBoxNonMember.vue';
import MemberBenefits from '../../common/components/member-benefits/MemberBenefits.vue';
import MabcPromo from './components/MabcPromo.vue';
import MemberPromoGroup from '../../common/components/member-promo/MemberPromoGroup.vue';
import MemberQuote from './components/MemberQuote.vue';
import CanPromo from '../../common/components/can-promo/CanPromo.vue';
import MembershipCoupons from '../../common/components/membership-coupons/MembershipCoupons.vue';
import MembershipPillars from '../../common/components/membership-pillars/MembershipPillars.vue';
import MemberStory from './components/MemberStory.vue';
import OverlappingCallout from './components/OverlappingCallout.vue';
import PillarModal from '../../common/components/modals/PillarModal.vue';
import RewardTerms from '../../common/components/member-reward-terms/RewardTerms.vue';
import YearInReviewCarousel from '../../common/components/year-in-review-carousel/YearInReviewCarousel.vue';
import FeaturedBenefitModal from '../../common/components/modals/FeaturedBenefitModal.vue';
import analyticsMixin from '../../common/mixins/analyticsMixin';
import setFilmstripSlidesVisible from '../../common/mixins/setFilmstripSlidesVisible';
import { metricsLink } from '../../utils/analytics';
import { PRODUCT_VIEW } from '../../utils/analytics-constants';
import { formatDate, formatRange } from '../../utils/formatDates';
import { getCartInformation } from '../../utils/cartController';
import { getPersonalizedMemberRewardInfo, getUserAccountData, getCustomerAttributes } from '../../utils/user-account-data';
import {
  getHeroInsetBoxModel,
  getMemberPromoGroupModel,
} from '../../utils/membership-benefits-adapter';
import {
  getMemberBenefits,
  getMabcPromoModel,
} from '../../utils/membership-benefits/member-benefits-adapter';
import { getYearInReviewSlidesCS } from '../../utils/membership-benefits/year-in-review-adapter';
import {
  getCreditCardCalloutModel,
  getTermsConditionsModel,
} from '../../utils/shared-adapters';
import RewardsBreakdown from '../../common/components/rewards-breakdown/RewardsBreakdown.vue';
import { getMemberMomentHeadline } from '../../utils/member-moment';
import { CAAS_DEFAULT_USER_ATTRIBUTES } from '../../utils/user-account-constants';

export default {
  name: 'MembershipBenefitsLanding',
  components: {
    AddedToCartModal,
    BenefitsAddToCart,
    CdrModal,
    CountdownBanner,
    CouponPlacement,
    CreditCardCallout,
    FeaturedBenefitModal,
    FilmstripCarousel,
    FrequentlyAskedQuestions,
    HomepageHeroComponent,
    InsetBoxMember,
    InsetBoxNonMember,
    MemberBenefits,
    MabcPromo,
    MemberPromoGroup,
    MemberQuote,
    CanPromo,
    MembershipCoupons,
    MembershipPillars,
    MemberStory,
    OverlappingCallout,
    PillarModal,
    RewardsBreakdown,
    RewardTerms,
    GenericHeroLead,
    YearInReviewCarousel,
  },
  mixins: [
    analyticsMixin,
    setFilmstripSlidesVisible,
  ],
  provide() {
    const homePageInjectables = {};
    const userInformation = {};

    Object.defineProperty(homePageInjectables, 'addMembershipToCart', {
      enumerable: true,
      get: () => (this.addMembershipToCartText || ''),
    });

    Object.defineProperty(homePageInjectables, 'isCartEmpty', {
      enumerable: true,
      get: () => !this.cartCount,
    });

    Object.defineProperty(userInformation, 'user', {
      enumerable: true,
      get: () => (this.user || {}),
    });

    return {
      homePageInjectables,
      userInformation,
      leadConfig: {
        earnEndDate: this.earnEndDate,
        spendAmount: this.spendAmount,
        earnRange: this.earnRange,
        redeemRange: this.redeemRange,
      },
    };
  },
  inheritAttrs: false,
  props: {
    abAkamaiTestData: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    additionalData: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    appProps: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    componentModels: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  setup(props) {
    const getPreviewUrl = () => '/membership/preview';
    const { content } = useDynamicContent(props, getPreviewUrl);
    if (content.value?.componentModels) {
      content.value = content.value.componentModels;
    }
    return {
      content,
    };
  },
  data() {
    return {
      addMembershipToCartError: {
        active: false,
        type: '',
        message: '',
      },
      atcModalOpenOverride: false,
      cartCount: 0,
      enableBuyNowButton: false,
      enableModalBuyNow: true,
      isClient: false,
      livePreviewHash: '',
      loading: true,
      memberRewardInfo: {
        earnedReward: null,
        lifetimeOrders: null,
        topCategory: null,
        totalYearsMember: null,
      },
      membershipAddedToCart: false,
      modal: {
        isOpen: false,
        containerModel: null,
        contentModel: null,
        type: '',
      },
      screenWidth: 375, // mobile-first ya dig
      user: {
        isMember: false,
        isLoggedIn: false,
      },
      userAttributes: CAAS_DEFAULT_USER_ATTRIBUTES,
    };
  },
  computed: {
    /* c8 ignore start */
    analyticsViewData() {
      return {
        ...PRODUCT_VIEW,
        pageSection: 'landing page',
      };
    },
    /* c8 ignore end */
    addMembershipToCartText() {
      return this.frontendStrings.addToCart;
    },
    creditCardCalloutModel() {
      const { cobrandCallouts } = this.content || {};
      return getCreditCardCalloutModel({
        ...cobrandCallouts,
        isMember: this.isMember,
        isLoggedIn: this.isLoggedIn,
        isLimitedTimeOfferActive: this.showLimitedTimeOffer,
      });
    },
    countdownBannerModel() {
      const {
        countdownBannerEyebrow: eyebrow,
        countdownBannerHeading: template,
        countdownBannerSubheading: subheading,
      } = this.frontendStrings;
      const { user } = this;
      const heading = getMemberMomentHeadline({ template, user });
      return { eyebrow, heading, subheading };
    },
    /*
     * Dynamically provides both the Component and the model data for the hero slot
     */
    computedHeroSlot() {
      if (this.showYearInReviewCarousel) {
        return {
          Component: YearInReviewCarousel,
          model: this.yearInReviewCarouselModel,
        };
      }
      return {
        Component: null,
        model: {},
      };
    },
    computedHeroClasses() {
      return {
        'hero--yir-active': this.showYearInReviewCarousel,
        'hero--2xR-active': true,
      };
    },
    earnEndDate() {
      const endDate = this.memberOfferInformation?.config?.earnEndDate;
      return endDate && formatDate(endDate);
    },
    earnRange() {
      if (!this?.memberOfferInformation?.config) return null;
      const { earnStartDate, earnEndDate } = this.memberOfferInformation?.config || {};
      return earnStartDate && earnEndDate && formatRange(earnStartDate, earnEndDate);
    },
    faq() {
      const { faq } = this.content || {};
      const preserveParagraphTag = {
        p: (attr, child) => (child === '' ? child : `<p>${child}</p>`),
      };
      const faqs = faq.entries.map((f) => ({
        question: f.question,
        answer: convertToHtml(f.answer, preserveParagraphTag),
      }));
      return {
        title: faq.title,
        questionsAndAnswers: faqs,
      };
    },
    featureToggles() {
      return this.appProps?.['feature.toggles'] || {};
    },
    filmstripModel() {
      const { filmstrip } = this.content;
      if (!filmstrip) return null;
      const {
        cards = [],
        cardStyle = '',
        heading = '',
        description = '',
        display = false,
      } = filmstrip;
      return {
        display,
        content: {
          heading: { text: heading, level: 'h2', size: 'sm-standard' },
          subheading: description,
        },
        slidesVisible: 3,
        slideStyle: cardStyle || 'Lifestyle 3:4 Large',
        overflow: true,
        slides: cards.map(({ image, link, copy }) => {
          const cta = link?.length ? link[0] : {};
          const { heading: h, subheading } = copy;
          const media = {
            src: image?.src,
            alt: image?.alt,
            renderedSizes: {
              mobile: '33vw',
              desktop: '25vw',
            },
          };
          return {
            props: {
              media,
              cta,
              heading: h,
              greenBannerBoldText: subheading,
            },
          };
        }),
      };
    },
    frontendStrings() {
      return this.appProps?.['frontend.strings'] || {};
    },
    frontendTestMaps() {
      return this.appProps?.['frontend.testMaps'] || {};
    },
    isCAASEnabled() {
      return this.appProps?.['cobrand.getCustomerAttributes.enabled'];
    },
    homepageHeroInsetBoxModel() {
      const { lead } = this.content || {};
      const { heroType } = this;
      return getHeroInsetBoxModel({ heroType, ...lead });
    },
    homepageHero() {
      const { lead } = this.content || {};
      const { loading } = this;
      const loginUrl = this.frontendStrings?.loginUrl;

      return {
        // evaluate whether to remove after MemMo
        countdownBannerModel: this.countdownBannerModel,
        screenWidth: this.screenWidth,
        showCountdownBanner: this.showCountdownBanner,
        img: lead?.image,
        loading,
        loginUrl,
      };
    },
    heroType() {
      if (this.isLoggedInMember) return 'MEMBER';
      if (this.isLoggedIn) return 'NON_MEMBER';
      return 'LOGGED_OUT';
    },
    insetBoxClasses() {
      return this.computedHeroSlot?.Component ? { class: 'inset-box--slot-active' } : {};
    },
    insetBoxMemberProps() {
      const { homepageHeroInsetBoxModel, memberRewardInfo } = this;
      const insetBoxMemberProps = {
        ...homepageHeroInsetBoxModel,
        ...this.insetBoxClasses,
      };
      if (memberRewardInfo?.topCategory) {
        insetBoxMemberProps.personalizedTopCategory = memberRewardInfo.topCategory;
      }
      return insetBoxMemberProps;
    },
    insetBoxNonMemberProps() {
      const { homepageHeroInsetBoxModel, frontendStrings } = this;
      return {
        ...homepageHeroInsetBoxModel,
        addToCartText: frontendStrings?.addToCart,
        enableBuyNowButton: this.enableBuyNowButton,
        heroType: this.heroType,
        membershipAddedToCart: this.membershipAddedToCart,
        ...this.insetBoxClasses,
      };
    },
    isMember() {
      return this.userAttributes?.member || this.user?.isMember;
    },
    isLoggedIn() {
      return this.user?.isLoggedIn;
    },
    isLoggedInMember() {
      return this.isLoggedIn && this.user?.isMember;
    },
    memberBenefitsModel() {
      const { memberBenefits } = this.content;
      const { benefits, ...rest } = memberBenefits;
      const { featured, standard } = getMemberBenefits(memberBenefits.benefits);
      return {
        featured,
        standard,
        ...rest,
      };
    },
    memberCouponModel() {
      return this.content.coupon;
    },
    memberMadeModel() {
      return {
        heading: '#REImember',
        body: 'Because your adventures are what make the co-op, photos featured on this page were captured by REI Co-op Members. Opt outside with @REI and members like you on Instagram.',
        image: {
          src: 'https://www.rei.com/dam/21222591_reimember_membershiplp.png',
          alt: 'photos of camping in the high desert by Angela R. & Yvonne L., R E I  Co-op Members',
        },
      };
    },
    mabcPromoModel() {
      return {
        ctaText: this.addMembershipToCartText,
        ...getMabcPromoModel(this.content.mabcPromo),
      };
    },
    showCanPromo() {
      return this.featureToggles?.showCanPromo;
    },
    memberMomemtEmbargoReleased() {
      return this.featureToggles.memMoEmbargoReleased;
    },
    memberPromoGroupModel() {
      const { memberOffers } = this.content;
      const promosExist = Array.isArray(memberOffers);
      if (!promosExist) return false;

      // UI only supports displaying the first three members offers, regardless of how many exist
      const firstThreeOffers = memberOffers.slice(0, 3);

      const model = {
        ...getMemberPromoGroupModel(firstThreeOffers),
        membershipInCart: this.membershipAddedToCart,
      };
      if (model.heading && this.user?.firstName) {
        model.heading = model.heading.replace('members', this.user.firstName);
      }
      return model;
    },
    memberQuote() {
      return this.content.memberQuote;
    },
    memberRewardTerms() {
      return getTermsConditionsModel(this.content.disclaimer);
    },
    memberStory() {
      return this.content.memberStory;
    },
    redeemRange() {
      if (!this.memberOfferInformation?.config) return null;
      const { redeemStartDate, redeemEndDate } = this.memberOfferInformation?.config || {};
      return redeemStartDate && redeemEndDate && formatRange(redeemStartDate, redeemEndDate);
    },
    resupplyModel() {
      return this.content.resupply;
    },
    showAddMembershipToCartModule() {
      // for MemMo, show to all users regardless of membership status
      const { featureToggles } = this;
      if (featureToggles?.showPreRewardsMessage || featureToggles?.isRewardsDropPeriod) {
        return true;
      }
      return !this.isMember;
    },
    showCountdownBanner() {
      const { countdownBannerModel: model, featureToggles } = this;
      const modelIsPresent = (model.eyebrow && model.heading && model.subheading);
      return modelIsPresent
        && (featureToggles?.showPreRewardsMessage || featureToggles?.isRewardsDropPeriod);
    },
    showFilmstrip() {
      return this.filmstripModel?.display;
    },
    showHomepageHero() {
      return !!(this.content.lead);
    },
    showMemberBenefits() {
      return !!this.content.memberBenefits;
    },
    showMembershipCoupons() {
      return this.featureToggles.enableCoupon;
    },
    showMemberStory() {
      return !!this.content.memberStory && !this.isMember;
    },
    showMabcPromo() {
      return !!this.content.mabcPromo && !this.isMember;
    },
    showMemberQuote() {
      return this.memberQuote && this.isMember;
    },
    showMemberRewardTerms() {
      // for MemMo, show to all users regardless of membership status
      const { featureToggles } = this;
      if (featureToggles?.showPreRewardsMessage || featureToggles?.isRewardsDropPeriod) {
        return true;
      }
      return !this.isMember;
    },
    showResupply() {
      return !!this.resupplyModel;
    },
    showYearInReviewCarousel() {
      return !!(this.content.lead?.showYearInReviewCarousel);
    },
    showLimitedTimeOffer() {
      return this.featureToggles?.showLimitedTimeOffer;
    },
    spendAmount() {
      const amount = this.memberOfferInformation?.config?.spendAmount;
      return amount && `$${amount}`;
    },
    showMemberCreditCardCallout() {
      // for MemMo, show to all users regardless of membership status
      if (this.featureToggles?.showLimitedTimeOffer) {
        return true;
      }
      return this.content.cobrandCallouts?.MEMBER
        && this.isMember
        && !this.isCardHolder;
    },
    trackingId() {
      const { isPromo } = this.content || false;
      if (isPromo) {
        return this.isMember ? 'dre_membership_lp-member' : 'dre_membership_lp-non_member';
      }

      return this.isMember ? 'membership_lp-member' : 'membership_lp-non_member';
    },
    yearInReviewCarouselModel() {
      const { heroType, memberRewardInfo } = this;
      const { lead } = this.content || {};
      const options = {
        heroType,
        memberRewardInfo,
        ...lead,
      };
      const slides = getYearInReviewSlidesCS(options);
      return {
        slides,
      };
    },
    isCardHolder() {
      return this.userAttributes?.reiccholder;
    },
  },
  watch: {
    isMember(next) {
      // page height changes when membership status is updated, so re-scroll to FAQ
      if (next && window?.location?.hash === '#faq') {
        this.scrollTo('faq');
      }
    },
  },
  async mounted() {
    if (typeof window !== 'undefined') {
      /** Hydration complete but contains mismatches work-around
       * We choose not to server-render CdrModal. CdrModal uses a Teleport component to hoist
         its DOM elements to be a child of <body/>. As a result, SSR doesn't return its
         server-rendered HTML in the response. This causes hydration mismatches.
       */
      this.isClient = true;

      // Window Resize EventListener
      window.addEventListener('resize', this.onResize);
      this.onResize();
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.onResize);
  },
  async created() {
    if (typeof window !== 'undefined') {
      const { isMembershipAddedToCart, cartCount } = await getCartInformation();
      this.cartCount = cartCount;
      this.membershipAddedToCart = isMembershipAddedToCart;
      if (this.isCAASEnabled) {
        const nextAttributes = await getCustomerAttributes();
        if (nextAttributes) this.userAttributes = nextAttributes;
      }
      try {
        getUserAccountData()
          .then((response) => {
            this.user = response;
            this.loading = false;
          })
          .then(this.enableBuyNow)
          .then(this.getYearInReviewUserData)
          .catch(console.error);
      } catch (error) {
        console.error(error);
      }
    }
  },
  methods: {
    enableBuyNow() {
      // if there is no membership in cart on page load and user is not a member
      // allow "buy now" functionality
      if (!this.isMember && !this.membershipAddedToCart) {
        this.enableBuyNowButton = true;
        this.enableModalBuyNow = false;

        let linkName = 'rei:member landing page_items in cart';
        if (!this.cartCount) {
          linkName = 'rei:member landing page_no cart items';
        }
        /* c8 ignore next */
        metricsLink(linkName);
      }
    },
    handleClose() {
      this.atcModalOpenOverride = false;
    },
    handleSuccess() {
      this.membershipAddedToCart = true;
      this.atcModalOpenOverride = true;
    },
    handleSharedModalOpen(type, model) {
      const containerModel = {
        label: model.heading,
        wrapperClass: `mem-modal mem-modal--${type}`,
      };
      if (type === 'featured-benefit') {
        containerModel.showTitle = false;
      }
      this.modal.containerModel = containerModel;
      this.modal.contentModel = model;
      this.modal.type = type;
      this.$nextTick(() => {
        this.modal.isOpen = true;
      });
    },
    handleModalClose() {
      this.modal.isOpen = false;
      setTimeout(() => {
        this.modal.containerModel = null;
        this.modal.contentModel = null;
        this.modal.type = '';
      }, 200);
    },
    getYearInReviewUserData() {
      if (this.isLoggedIn) {
        getPersonalizedMemberRewardInfo()
          .then((response) => {
            if (response) {
              const {
                totalYearsMember,
                topCategory,
                earnedReward,
                lifetimeOrders,
              } = response;
              this.memberRewardInfo = {
                totalYearsMember,
                topCategory,
                earnedReward,
                lifetimeOrders,
              };
            }
          });
      } else {
        console.warn('cannot get YIR user data, user not logged in');
      }
    },
    onResize() {
      if (typeof window !== 'undefined' && window.document) {
        const clientWidth = document?.body?.clientWidth;
        if (clientWidth) this.screenWidth = clientWidth;
      }
    },
    /* c8 ignore start */
    handleError(message) {
      if (window && window.metrics) {
        metricsLink(`error_add membership to cart_${message}`);
      }
    },
    handleWarn(message) {
      if (window && window.metrics) {
        metricsLink(`warn_add membership to cart_${message}`);
      }
    },
    handleFAQClick({ action, index }) {
      if (action === 'open') {
        const { question = '' } = this.faq.questionsAndAnswers[index] || {};
        metricsLink(`faq_${index}_${question}`);
      }
    },
    scrollTo(id) {
      this.$nextTick(() => {
        if (typeof document !== 'undefined' && document?.getElementById) {
          document.getElementById(id).scrollIntoView({
            behavior: 'smooth',
          });
        }
      });
    },
    /* c8 ignore end */
  },
};
</script>

<style lang="scss">

html {
  scroll-behavior: smooth;
}

.membership-benefits {
  overflow: hidden;
  min-height: 100vh;
}

.membership-benefits__heading {
  &#{&} {
    @include cdr-text-heading-serif-700;
  }
}

.filmstrip-carousel {
  @include cdr-container;
  margin-bottom: $cdr-space-three-x;
  @include cdr-lg-mq-up {
    margin-bottom: 10rem;
  }
}
.ol-callout--used-gear {
  @include cdr-lg-mq-up {
    margin-bottom: $cdr-space-three-x;
  }
}

.membership-benefits__heading--challenger {
  @include cdr-text-heading-serif-700;
}
.membership-benefits__add-membership {
  @include cdr-container;
}
.membership-benefits__member-quote {
  background-color: $cdr-color-background-secondary;
  padding-top: $cdr-space-three-x;
  padding-bottom: $cdr-space-three-x;
}
.faq-container {
  padding-bottom: $cdr-space-one-and-a-half-x;
  // overrides main min css. Needed for accessibility
  div[class^='cdr-accordion__content_'] p a {
    text-decoration: underline;
  }

  @include cdr-lg-mq-up {
    padding-bottom: $cdr-space-three-x;
  }
}
// give member benefits list marging if it appears directly after hero
.membership-benefits__hero-wrapper + .member-benefits {
  @include cdr-lg-mq-up {
    margin-top: $cdr-space-four-x;
  }
}
</style>
